import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Toon } from "@/components/Toon";
import { partnerHref } from "@/lib/links";

const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay, testId }) => (
  <span className="block overflow-hidden pb-3">
    <motion.span
      data-testid={testId}
      className="block will-change-transform leading-[1.04]"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const portraitY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-40 pb-16 lg:pt-52"
    >
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <img
          src="https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4ODA0MjI5NHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-[125%] w-full object-cover opacity-[0.12]"
        />
      </motion.div>

      <div className="mx-auto grid w-full max-w-[90rem] grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="mb-6 text-xs uppercase tracking-[0.35em] text-ash"
          >
            Gerard Wiley · CRM, LLC · 19+ Years
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-[clamp(2.4rem,5.4vw,5rem)] font-medium leading-[1.04] tracking-tight text-fog"
          >
            <MaskedLine delay={0.25} testId="hero-line-insurance">
              Insurance<span className="align-middle text-[0.5em]">🛡️</span>
            </MaskedLine>
            <MaskedLine delay={0.37} testId="hero-line-energy">
              Energy<span className="align-middle text-[0.5em]">⚡</span>
            </MaskedLine>
            <MaskedLine delay={0.49} testId="hero-line-finance">
              Finance<span className="align-middle text-[0.5em]">📊</span>
            </MaskedLine>
            <MaskedLine delay={0.61} testId="hero-line-future">
              <span className="italic text-steel">Payments</span><span className="align-middle text-[0.5em]">💳</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ash md:text-lg"
          >
            19+ years putting commercial problem-solving on your side of the
            table — lower benefit costs, cleaner energy, funding that fits,
            and best-in-class payment card processing. When in need —
            CallMe📲Gerard.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.0 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              data-testid="hero-call-cta"
              href="tel:3177076818"
              className="inline-flex items-center gap-3 rounded-full bg-navy px-8 py-4 text-xs uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-steel"
            >
              Call (317) 707-6818
            </a>
            <a
              data-testid="hero-schedule-cta"
              href={partnerHref("https://calendly.com/callmegerard/lets-talk", "Schedule a meeting")}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-fog"
            >
              <span className="border-b border-white/20 pb-1 transition-colors duration-300 group-hover:border-steel">
                Schedule a meeting
              </span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
            className="relative mx-auto max-w-md"
          >
            <div className="absolute -inset-12 rounded-full bg-navy/25 blur-[110px]" />
            <motion.div style={{ y: portraitY }} className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full border border-steel/30" />
              <div className="absolute -bottom-4 -left-4 h-full w-full border border-white/5" />
              <picture>
                <source srcSet="/gerard-headshot.webp" type="image/webp" />
                <img
                  data-testid="hero-portrait"
                  src="/gerard-headshot.png"
                  alt="Gerard Wiley — Insurance, Energy, Finance, Future"
                  className="relative aspect-[3/4] w-full object-cover"
                />
              </picture>
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-white/10 bg-black/50 px-5 py-3 backdrop-blur-md">
                <span className="text-[10px] uppercase tracking-[0.3em] text-ash">
                  When in need
                </span>
                <span className="font-serif italic text-fog">
                  CallMe📲Gerard
                </span>
              </div>
              <Toon
                testId="hero-toon-badge"
                variant="float"
                imgClass="w-24 md:w-40"
                className="absolute -top-10 -left-10 z-10"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 md:left-12"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-steel" />
        <span
          data-testid="hero-scroll-hint"
          className="text-[10px] uppercase tracking-[0.35em] text-dim"
        >
          Scroll
        </span>
      </motion.div>
    </section>
  );
}
